import { useEffect, useRef, useState } from "react";
import { Briefcase, Plus, Trash2, Phone, Search, X, UserCheck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Outside Vendors panel — manually-entered vendor records (NOT from
 * CampMinder). Admin types first name, last name, phone → vendor is saved
 * as a lightweight crew_members row with `is_vendor: true`. From there:
 *   • Vendor signs in at the kiosk like staff (matched by name + phone).
 *   • Their sign-in / sign-out times appear on the EOD report's Vendors tab.
 *   • They are skipped on the main attendance dashboard (the "Vendors"
 *     division is in EXCLUDED_DIVISIONS, so they show "—" instead of
 *     inflating "Absent").
 *   • CampMinder sync never touches them (source = "manual_vendor").
 */
export default function VendorRosterPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");

  // "Mark existing staff as vendor" typeahead
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const dropRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/vendors");
      setItems(r.data.items || []);
    } catch {
      toast.error("Failed to load Vendors");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Typeahead — search existing staff that aren't already vendors.
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggest(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/staff-cards", { params: { search: query.trim() } });
        const flat = [];
        for (const g of r.data.groups || []) {
          for (const m of g.members || []) {
            flat.push({
              crew_id: m.crew_id,
              name: m.name,
              division: m.division,
              position: m.position,
            });
          }
        }
        const alreadyVendor = new Set(items.map((i) => i.crew_id));
        const filtered = flat
          .filter((m) => !alreadyVendor.has(m.crew_id))
          .filter((m) => (m.division || "").toLowerCase() !== "vendors")
          .slice(0, 12);
        setSuggestions(filtered);
        setShowSuggest(true);
      } catch {
        setSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query, items]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Mark an existing staff member as a vendor (preserves their original
  // division so /admin/vendors DELETE can restore them later).
  const promote = async (m) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/vendors/from-staff", { crew_id: m.crew_id });
      if (r.data?.already_vendor) toast.info(`${m.name} is already a vendor`);
      else toast.success(`Marked ${m.name} as vendor (was: ${r.data?.original_division || "—"})`);
      setQuery("");
      setSuggestions([]);
      setShowSuggest(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark as vendor");
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    const fn = firstName.trim();
    const ln = lastName.trim();
    const ph = phone.trim();
    if (!fn || !ln) {
      toast.error("First and last name are required");
      return;
    }
    if (ph.replace(/\D/g, "").length < 7) {
      toast.error("Phone number needs at least 7 digits");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/vendors", { first_name: fn, last_name: ln, phone: ph });
      toast.success(`Added ${fn} ${ln}`);
      setFirstName("");
      setLastName("");
      setPhone("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Remove vendor ${it.name}? Their past sign-ins will be kept.`)) return;
    try {
      await api.delete(`/admin/vendors/${encodeURIComponent(it.crew_id)}`);
      toast.success(`Removed ${it.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  const formatPhone = (raw) => {
    const d = (raw || "").replace(/\D/g, "");
    if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
    if (d.length === 11 && d[0] === "1") return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
    return raw || "";
  };

  return (
    <section className="p-6" data-testid="vendor-roster-section">
      <div className="mb-1">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-slate-700" /> Outside Vendors
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Type each vendor&apos;s name and phone manually. Once added they can sign in at the kiosk
          with their name + phone like staff. Their sign-in / sign-out times appear on the EOD
          report&apos;s <strong>Vendors</strong> tab. Vendors don&apos;t count toward staff attendance totals.
        </p>
      </div>

      {/* Mark existing staff as vendor — typeahead from current roster. */}
      <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50/40 p-4">
        <div className="flex items-center gap-2 mb-2">
          <UserCheck className="w-4 h-4 text-blue-700" />
          <h3 className="font-semibold text-sm text-blue-900">Mark existing staff as vendor</h3>
        </div>
        <p className="text-xs text-slate-600 mb-3">
          For people already in the system (e.g. pulled from CampMinder) who are actually outside vendors.
          Their original division is saved so you can un-flag them later.
        </p>
        <div className="relative" ref={dropRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              placeholder="Start typing a name (e.g. Dallas Serrano)…"
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggest(true)}
              className="w-full pl-9 pr-9 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="vendor-promote-search-input"
              autoComplete="off"
              disabled={busy}
            />
            {query && (
              <button
                onClick={() => { setQuery(""); setShowSuggest(false); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                data-testid="vendor-promote-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {showSuggest && suggestions.length > 0 && (
            <ul
              className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg"
              data-testid="vendor-promote-suggestions"
            >
              {suggestions.map((m) => (
                <li key={m.crew_id}>
                  <button
                    onClick={() => promote(m)}
                    disabled={busy}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-b-0 flex items-center justify-between gap-2"
                    data-testid={`vendor-promote-suggest-${m.crew_id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 text-sm truncate">{m.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {m.division || "—"}{m.position ? ` · ${m.position}` : ""}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-600 text-white text-xs font-semibold shrink-0">
                      <UserCheck className="w-3 h-3" /> Flag
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {showSuggest && query.trim().length >= 2 && suggestions.length === 0 && (
            <div className="absolute z-20 mt-1 w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs text-slate-500">
              No matches{items.find((i) => (i.name || "").toLowerCase().includes(query.trim().toLowerCase())) ? " (already on vendor list)" : ""}.
            </div>
          )}
        </div>
      </div>

      {/* Add form */}
      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Plus className="w-4 h-4 text-slate-700" />
          <h3 className="font-semibold text-sm text-slate-900">Add a new vendor (not in the system yet)</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">First name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="John"
              className="w-full py-2 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="vendor-first-input"
              autoComplete="off"
              disabled={busy}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Last name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Smith"
              className="w-full py-2 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="vendor-last-input"
              autoComplete="off"
              disabled={busy}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Phone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="(555) 555-1234"
              className="w-full py-2 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="vendor-phone-input"
              autoComplete="off"
              disabled={busy}
            />
          </div>
          <div className="flex md:items-end">
            <button
              onClick={add}
              disabled={busy || !firstName.trim() || !lastName.trim() || !phone.trim()}
              className="w-full md:w-auto btn-touch inline-flex items-center justify-center gap-2 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold"
              data-testid="vendor-add-btn"
            >
              <Plus className="w-4 h-4" /> Add Vendor
            </button>
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Tip: vendors sign in at the kiosk with this exact phone — make sure it matches what they&apos;ll type.
        </p>
      </div>

      {/* List */}
      <div className="mt-6">
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">Loading vendors…</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500" data-testid="vendor-empty">
            No vendors yet. Use the form above to add your first one.
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Name</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Phone</th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.crew_id} className="border-t border-slate-100" data-testid={`vendor-row-${it.crew_id}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {it.last_name || ""}{it.last_name && it.first_name ? ", " : ""}{it.first_name || ""}
                      {(!it.last_name && !it.first_name) && it.name}
                    </td>
                    <td className="px-4 py-3 text-slate-700 tabular-nums">
                      <span className="inline-flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-slate-400" />
                        {formatPhone(it.phone)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => remove(it)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200"
                        data-testid={`vendor-remove-${it.crew_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="mt-3 text-xs text-slate-500">{items.length} vendor{items.length === 1 ? "" : "s"}</div>
        )}
      </div>
    </section>
  );
}
